import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/ui/Icon';
import { useGatingStore } from '@/stores/gating.store';
import { focusSurface } from '@/components/ui/focusStyles';
import { cn } from '@/utils/cn';
import { LockBadge } from './LockBadge';
import { getFeatureCopy, getPlanDisplayName } from './gatingCopy';

interface LockedTeaserRowProps {
  /** Registry / portfolio feature key, e.g. 'portfolio_highlights' */
  feature: string;
  /** HappyIcons name for the row */
  icon: string;
  className?: string;
}

/**
 * Slim one-row teaser for a section that is really locked by the plan: icon, title, "Available on {plan}",
 * lock badge. A click force-opens the existing upsell. The caller decides WHEN it is locked (resolver of its scope).
 */
export const LockedTeaserRow = ({ feature, icon, className }: LockedTeaserRowProps) => {
  const { t } = useTranslation('common');
  const openUpsell = useGatingStore((state) => state.openUpsell);
  const slug = useGatingStore((state) => state.lockedSlugs[feature] ?? null);
  const title = getFeatureCopy(t, feature).title;
  const planName = getPlanDisplayName(slug);
  const label = planName ? t('gating_available_on', { plan: planName }) : t('gating_available_on_generic');
  return (
    <button
      type="button"
      onClick={() => openUpsell({ feature, upsellPlanSlug: slug }, true)}
      data-testid={`locked-entry-${feature}`}
      aria-label={t('gating_locked_label', { feature: title })}
      className={cn(
        'w-full flex items-center gap-3 border border-gray-200 bg-white px-4 py-2.5 text-left transition-colors hover:border-primary-300',
        focusSurface,
        className
      )}
    >
      <Icon name={icon} size="md" color="text-primary-500" ariaHidden />
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold text-gray-900 truncate">{title}</span>
        <span className="block text-xs font-medium text-primary-700 truncate">{label}</span>
      </span>
      <LockBadge />
    </button>
  );
};

export default LockedTeaserRow;
